import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { ObjectIdString } from "../../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { ForeignLookup } from "../../commonSubSchemas/foreignLookup.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    NotificationSubscriptionRuleFieldsWithDefaults,
    NotificationSubscriptionRuleSchemaTypes,
} from "../../types/notificationSubscriptionRule.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import { NOTIFICATION_SCOPE_COLLECTIONS, SCOPE_TARGET_FOREIGN_FIELD } from "../notificationScopes.js";
import { WarningTypeNotificationMode } from "./warningTypeNotificationMode.js";

const SCHEMA_KEY = "NotificationSubscriptionRule" as const;

/**
 * A rule scope: a ForeignLookup pinned to the scope collections, resolved by `_id`. One shape for reads
 * and writes alike — every stored scope was written through it, so responses re-assert the same contract.
 */
const notificationScopeValidator = z.object({
    // ObjectIdString is the repo's ObjectId contract, so a malformed targetId 400s at parse alongside the
    // other two fields. It yields an ObjectId; ForeignLookup.targetId is a string, so it goes back to one
    // rather than rippling an ObjectId through the write path.
    targetId: ObjectIdString.transform(String),
    targetForeignField: z.literal(SCOPE_TARGET_FOREIGN_FIELD),
    targetCollection: z.enum(NOTIFICATION_SCOPE_COLLECTIONS),
});

/**
 * The routing atom: one or more scopes (each an org or site — the warning's subject, as a
 * ForeignLookup) plus the warning types to alert there and how loudly. A warning matches when any
 * scope matches. Types not listed in `notificationModes` are muted. A rule is meaningful only within
 * its subscription.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<NotificationSubscriptionRule>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NotificationSubscriptionRule extends SchemaBase<
    NotificationSubscriptionRule,
    NotificationSubscriptionRuleFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * The scopes this rule alerts on: each `targetCollection` ∈ { organizations, areaOfInterest },
     * matched against the warning's corresponding scope key (`owner` / `aoi`). Every scope must
     * belong to the subscription's `owner` (enforced at the write boundary). At least one entry.
     * Client-writable, so the DTOs pin the wire shape rather than accepting any ForeignLookup: an unknown
     * collection or a targetForeignField other than `_id` is a parse-time 400 on writes, and on responses
     * too — the contract is asserted in both directions.
     */
    @validate({ zodValidator: z.array(notificationScopeValidator).min(1) })
    @prop({ type: () => [ForeignLookup], required: true, _id: false }, PropType.ARRAY)
    public scopes!: ForeignLookup[];

    /**
     * The types to alert on at this rule's scopes and how loudly; an unlisted type is muted. No
     * min-cardinality: an empty list is a legal rule that mutes every type, so a scopes-only rule is
     * accepted and simply never fires.
     */
    @validate({
        childClass: WarningTypeNotificationMode,
        childClassTransform: (object) => z.array(object),
        hasDefault: true,
    })
    @prop({ type: () => [WarningTypeNotificationMode], required: true, default: [], _id: false }, PropType.ARRAY)
    public notificationModes: WarningTypeNotificationMode[] = [];

    static getters = {} as const satisfies Record<string, (self: NotificationSubscriptionRule) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NotificationSubscriptionRuleSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            NotificationSubscriptionRule,
            NotificationSubscriptionRuleFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

NotificationSubscriptionRule.dtoValidators = getZodSchemaFromClass(
    NotificationSubscriptionRule,
) as unknown as DtoValidatorTypes<NotificationSubscriptionRuleSchemaTypes>;
